// Spec 45 component-behavior — cmp_ds_radio + aria_radio_group.
//
// Two surfaces:
//   * Radio — single button. Native <input type="radio">. The `name`
//     attribute is what groups them; consumer wraps multiple Radio in
//     a fieldset/RadioGroup providing a shared `name`.
//   * RadioGroup — fieldset wrapper providing a name + role="radiogroup"
//     + aria-labelledby. Optional but encouraged — without it the
//     consumer must wire the keyboard arrow-navigation themselves.
//     RadioGroup handles arrow keys per spec 45 aria_radio_group.
//
// Keyboard model on a focused radio in a group:
//   * Arrow Right/Down — move focus + check next radio in DOM order
//   * Arrow Left/Up — move focus + check previous
//   * Tab — leaves the group entirely (only one radio in tab order)
//   * Space — checks the focused radio (native)

import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  type FieldsetHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  readonly error?: boolean;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ error = false, disabled, className, ...rest }, ref) => (
    // aria-invalid is NOT a valid property on role=radio per WAI-ARIA;
    // form validation invalidity is communicated via the parent
    // RadioGroup's aria-invalid (set its `error` prop). The Radio
    // surfaces the error visually via data-error so the consumer's
    // CSS can paint the affordance.
    <input
      ref={ref}
      type="radio"
      data-component="radio"
      data-error={error || undefined}
      disabled={disabled === true}
      className={cx("nai-radio", className)}
      {...rest}
    />
  ),
);
Radio.displayName = "Radio";

export interface RadioGroupProps
  extends Omit<FieldsetHTMLAttributes<HTMLFieldSetElement>, "name"> {
  /** Shared name applied to every Radio child. Required. */
  readonly name: string;
  /** Accessible label id — points at the legend or external label. */
  readonly "aria-labelledby"?: string;
  /** When true, sets aria-invalid on the radiogroup (the WAI-ARIA-correct surface for radio invalidity). */
  readonly error?: boolean;
  readonly children?: ReactNode;
}

export const RadioGroup = forwardRef<HTMLFieldSetElement, RadioGroupProps>(
  ({ name, "aria-labelledby": labelledBy, error = false, onKeyDown, className, children, ...rest }, ref) => {
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLFieldSetElement>) => {
        const radios = Array.from(
          event.currentTarget.querySelectorAll<HTMLInputElement>(
            'input[type="radio"]:not(:disabled)',
          ),
        );
        if (radios.length === 0) {
          onKeyDown?.(event);
          return;
        }
        const target = event.target as HTMLInputElement | null;
        if (target === null || !radios.includes(target)) {
          onKeyDown?.(event);
          return;
        }
        const idx = radios.indexOf(target);
        let nextIdx = -1;
        if (event.key === "ArrowDown" || event.key === "ArrowRight") {
          nextIdx = (idx + 1) % radios.length;
        } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
          nextIdx = (idx - 1 + radios.length) % radios.length;
        }
        if (nextIdx >= 0) {
          event.preventDefault();
          const next = radios[nextIdx];
          if (next !== undefined) {
            next.focus();
            next.click();
          }
          return;
        }
        onKeyDown?.(event);
      },
      [onKeyDown],
    );

    // Inject the shared name onto every Radio child. Avoids the
    // consumer having to repeat name="X" on every Radio.
    const childrenWithName = Children.map(children, (child) => {
      if (!isValidElement(child)) return child;
      // Only inject onto Radio components — guard via component identity.
      if ((child.type as { displayName?: string })?.displayName !== "Radio") return child;
      return cloneElement(child as ReactElement<RadioProps>, { name });
    });

    return (
      <fieldset
        ref={ref}
        role="radiogroup"
        aria-labelledby={labelledBy}
        aria-invalid={error || undefined}
        data-component="radio-group"
        data-error={error || undefined}
        className={cx("nai-radio-group", className)}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {childrenWithName}
      </fieldset>
    );
  },
);
RadioGroup.displayName = "RadioGroup";
